/**
 * cypAdhdMseConfig.js — Mental State Examination structure for the CYP ADHD
 * Clinical Interview.
 *
 * UNCHANGED FROM ADULT (decision, 22 Aug): the subsections, categories and
 * option lists below are transcribed verbatim from the adult ADHD MSE
 * (python-functions/constants/assessment_data_model.py, section 10). Do not
 * reword options here — the clinical team reviews MSE wording on the adult
 * pathway and CYP mirrors it.
 *
 * Data shape written to the ci stage document (field "mse"):
 *   mse.{subsectionVarName}.{categoryName} = {
 *     isAppropriateSelection: boolean | null,
 *     selectionDetails: string[],
 *     subcategories?: { [optionName]: { [subCategoryName]: { selectionDetails: string[] } } },
 *   }
 *   mse.mood.description = string   (free-text subsection)
 *
 * Mirrors the adult storage shape so downstream consumers (LLM inputs,
 * report rendering) can share formatting logic.
 */

export const CYP_MSE_SUBSECTIONS = [
  {
    varName: 'appearanceAndBehaviour',
    title: 'Appearance and Behaviour',
    type: 'clickthrough',
    categories: [
      { name: 'Rapport', yesOptions: ['Normal, rapport easily established'], noOptions: ['Difficult to establish rapport', 'Rapport absent'] },
      { name: 'Cooperativeness', yesOptions: ['Normal, cooperative'], noOptions: ['Uncooperative', 'Guarded', 'Defensive', 'Suspicious', 'Hostile', 'Overfamiliar', 'Apathetic'] },
      { name: 'Appearance', yesOptions: ['Well-groomed'], noOptions: ['Poorly Groomed', 'Disheveled', 'Unkempt', 'Inappropriate for age/situation', 'Inappropriate attire'] },
      { name: 'Hygiene', yesOptions: ['Average'], noOptions: ['Unclean', 'Malodorous'] },
      { name: 'Self-Care', yesOptions: ['Good'], noOptions: ['Neglected'] },
      { name: 'Eye Contact', yesOptions: ['Normal'], noOptions: ['Avoidant', 'Limited', 'Variable', 'Staring'] },
      { name: 'Posture', yesOptions: ['Normal'], noOptions: ['Relaxed', 'Slouched / Slumped', 'Rigid'] },
      { name: 'Psychomotor Activity', yesOptions: ['Normal'], noOptions: ['Decreased', 'Agitated', 'Fidgety', 'Restless', 'Pacing', 'Overactive'] },
      { name: 'Physical Behaviour', yesOptions: ['Normal'], noOptions: ['Restlessness', 'Stereotypies or tics'] },
      { name: 'Demeanour', yesOptions: ['Normal'], noOptions: ['Restless', 'Agitated', 'Calm', 'Aggressive/hostile', 'Overfamiliar (e.g. inappropriate touching)'] },
      { name: 'Gait & Fine motor function', yesOptions: ['Normal gait and upper body movements'], noOptions: ['Slow or halting gait/movements', 'Shuffling gait', 'Poor balance', 'Tremors', 'Rigidity'] },
    ],
  },
  {
    varName: 'speech',
    title: 'Speech',
    type: 'clickthrough',
    categories: [
      { name: 'Rate', yesOptions: ['Appropriate, controlled'], noOptions: ['Rapid', 'Slow', 'Pressured', 'Deliberate', 'Monotonous'] },
      { name: 'Volume', yesOptions: ['Appropriate, audible'], noOptions: ['Loud', 'Soft, mute'] },
      { name: 'Tone', yesOptions: ['Normal'], noOptions: ['Monotone', 'High pitched'] },
      { name: 'Rhythm', yesOptions: ['Normal, fluent with variation'], noOptions: ['Monotonous', 'Staccato'] },
      { name: 'Fluency', yesOptions: ['Normal, fluent'], noOptions: ['Halting', 'Word-finding difficulty', 'Pressured speech', 'Garrulous', 'Overly-talkative', 'Paucity of speech'] },
      { name: 'Quantity', yesOptions: ['Appropriate'], noOptions: ['Monosyllables, Minimal', 'Detailed, Elaborate', 'Concise'] },
      { name: 'Quality', yesOptions: ['Appropriate, Clear'], noOptions: ['Stutters', 'Slurred', 'Mumble'] },
      { name: 'Spontaneity', yesOptions: ['Appropriate'], noOptions: ['Spontaneous', 'Minimal spontaneous utterances', 'Circumstantial (overinclusion of detail)', 'Inappropriate'] },
      { name: 'Speech & Language', yesOptions: ['Normal'], noOptions: ['Neologisms (makes up new words)', 'Anomia (difficulty naming objects)', 'Word Salad (incomprehensible speech)', 'Apraxia (difficulty carrying out sequential speech)', 'Clang associations (meaningless word rhymes)', 'Echolalia (word echoing)', 'Incoherent', 'Aphasia (impairment of language)'] },
    ],
  },
  {
    varName: 'mood',
    title: 'Mood',
    type: 'freeText',
    tooltipText: "Mood represents the patient's predominant internal emotional state as described by them. It is subjective and should be recorded in their own words.",
    freeTextLabel: "Patient's description of Mood",
  },
  {
    varName: 'affect',
    title: 'Affect',
    type: 'clickthrough',
    categories: [
      { name: 'Range of Emotional Expression', yesOptions: ['Appropriate'], noOptions: ['Blunted', 'Flat', 'Tearful', 'Restricted', 'Irritable', 'Sad', 'Expansive', 'Manic', 'Tearful', 'Crying'] },
      { name: 'Appropriateness', yesOptions: ['Appropriate, congruent with mood'], noOptions: ['Incongruent'] },
      { name: 'Stability', yesOptions: ['Appropriate, stable'], noOptions: ['Labile'] },
      { name: 'Intensity', yesOptions: ['Normal'], noOptions: ['Exaggerated', 'Reduced'] },
    ],
  },
  {
    varName: 'thoughtForm',
    title: 'Thought Form',
    type: 'clickthrough',
    categories: [
      { name: 'Productivity', yesOptions: ['Average'], noOptions: ['Spontaneous', 'Over abundance', 'Needs prompting', 'Paucity', 'Mixed'] },
      { name: 'Continuity', yesOptions: ['Average'], noOptions: ['Goal Directed, Logical, Perceptive', 'Perseveration or Repetition of thoughts', 'Irrelevant to question asked'] },
    ],
  },
  {
    varName: 'thoughtContent',
    title: 'Thought Content',
    type: 'clickthrough',
    categories: [
      { name: 'Thought Disturbances', yesOptions: ['None'], noOptions: ['Delusions: Primary (arising without any known cause)', 'Delusions: Secondary (as a response to other mental states)', 'Delusions: Grandiose', 'Delusions: Paranoia', 'Delusions: Persecutory', 'Overvalued ideas (believes unrelated events refer to them)', 'Thought insertion (thoughts placed into their mind by others)', 'Thought withdrawal (thoughts taken away)', 'Thought broadcasting (thoughts being broadcast to others)'] },
      { name: 'Preoccupation', yesOptions: ['None'], noOptions: ['Obsessions', 'Compulsions', 'Phobias', 'Effort to go from thought to thought', 'Loose associations, Rambling', 'Tangential', 'Blocking, Vacant stare', 'Flight of ideas', 'Circumstantial or Roundabout Thinking'] },
    ],
  },
  {
    varName: 'perception',
    title: 'Perception',
    type: 'clickthrough',
    categories: [
      {
        name: 'Perception',
        yesOptions: ['None'],
        noOptions: ['Illusions (misinterpretations of real stimuli)', 'Derealisation (feeling the external world is unreal)', 'Depersonalisation (feeling detached from oneself)', 'Hallucinations'],
        subcategories: {
          Hallucinations: {
            Type: ['First-person auditory hallucinations (hearing own thoughts spoken aloud)', 'Second-person auditory hallucinations (voices addressing the patient)', 'Third-person auditory hallucinations (voices talking about the patient in the third person)', 'Visual', 'Tactile', 'Olfactory'],
            Quality: ['Internal (subjective)', 'External (objective)', 'Under individual control', 'No control exerted'],
            Nature: ['Commanding (risk assessment required)', 'Commenting on thoughts/actions', 'Neutral'],
          },
        },
      },
    ],
  },
  {
    varName: 'cognition',
    title: 'Cognition',
    type: 'clickthrough',
    categories: [
      { name: 'Orientation', yesOptions: ['Normal'], noOptions: ['Partially oriented to person', 'Partially oriented to place', 'Partially oriented to time', 'Disoriented'] },
      { name: 'Attention', yesOptions: ['Normal, intact'], noOptions: ['Impaired'] },
      { name: 'Concentration', yesOptions: ['Normal, intact'], noOptions: ['Impaired'] },
      { name: 'Memory: Immediate recall', yesOptions: ['Normal, intact'], noOptions: ['Impaired'] },
      { name: 'Memory: Recent memory', yesOptions: ['Normal, intact'], noOptions: ['Impaired'] },
      { name: 'Memory: Remote memory', yesOptions: ['Normal, intact'], noOptions: ['Impaired'] },
    ],
  },
  {
    varName: 'insightAndCapacity',
    title: 'Insight and Capacity',
    type: 'clickthrough',
    categories: [
      { name: 'Insight', yesOptions: ['Normal, full'], noOptions: ['Partial', 'Poor', 'None'] },
      { name: 'Judgement', yesOptions: ['Normal, intact'], noOptions: ['Impaired', 'Poor'] },
      { name: 'Ability to Consent', yesOptions: ['Normal, intact'], noOptions: ['Impaired'] },
    ],
  },
];
